/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Truck,
  Package,
  Clock,
  CheckCircle,
  Phone,
  Mail,
  Globe,
  X,
  AlertCircle,
  Download,
  Share2,
  MapPin,
  Warehouse,
  PackageCheck,
  Route,
  Boxes,
  Container
} from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'
// import Faq from './Faq'
import whatsApp from '../assets/icons/whatsapp.png'
import Gmail from '../assets/icons/gmail.png'
import Telegram from '../assets/icons/telegram.png'
import { normalizeTrackingData } from '../utils/trackingParser'
import FooterPage from './FooterPage'

const API_URL = process.env.REACT_APP_BACKEND_URL

const STATUS_ORDER = [
  'Pod Received',
  'Delivered',
  'Cancelled',
  'Delivery Attempted',
  'Out for Delivery',
  'Reached Delivery Branch',
  'Arrived at Delivery Location',
  'Reached Destination',
  'In Transit',
  'Departed from Facility',
  'Arrived at Hub',
  'Arrived at Pickup Location',
  'Reached at Pickup Branch',
  'Delayed',
  'Pending',
  'Manifested',
  'Tracking Update'
]

const STATUS_COLORS = {
  delivered: '#15803d',
  'pod received': '#7c3aed',
  cancelled: '#be123c',
  'delivery attempted': '#ea580c',
  'out for delivery': '#d97706',
  'reached delivery branch': '#1d4ed8',
  'arrived at delivery location': '#2563eb',
  'reached destination': '#4f46e5',
  'in transit': '#2563eb',
  'departed from facility': '#4f46e5',
  'arrived at hub': '#0284c7',
  'arrived at pickup location': '#0891b2',
  'reached at pickup branch': '#0d9488',
  delayed: '#dc2626',
  pending: '#64748b',
  manifested: '#0d9488',
  'tracking update': '#64748b'
}

const getStatusIcon = status => {
  const value = (status || '').toLowerCase()

  if (value.includes('deliver') && value !== 'delivery attempted')
    return <CheckCircle size={17} />
  if (
    value.includes('attempted') ||
    value.includes('cancel') ||
    value.includes('delay')
  )
    return <AlertCircle size={17} />
  if (value.includes('pickup') || value.includes('picked'))
    return <Package size={17} />
  if (
    value.includes('transit') ||
    value.includes('departed') ||
    value.includes('facility')
  )
    return <Truck size={17} />
  if (value.includes('pending')) return <Clock size={17} />
  if (value.includes('manifest')) return <Package size={17} />
  if (
    value.includes('branch') ||
    value.includes('hub') ||
    value.includes('destination') ||
    value.includes('location')
  )
    return <MapPin size={17} />

  return <Globe size={17} />
}

const formatTime = value => {
  if (!value) return ''

  const months = {
    JAN: '01',
    FEB: '02',
    MAR: '03',
    APR: '04',
    MAY: '05',
    JUN: '06',
    JUL: '07',
    AUG: '08',
    SEP: '09',
    OCT: '10',
    NOV: '11',
    DEC: '12'
  }

  const cleaned = value.replace(
    /(\d{2})-([A-Z]{3})-(\d{4})/,
    (_, day, month, year) => `${year}-${months[month]}-${day}`
  )

  const date = new Date(cleaned)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function TrackingPage () {
  const { lrno } = useParams()
  const [trackingNumber, setTrackingNumber] = useState(lrno || '')
  const [trackingSteps, setTrackingSteps] = useState([])
  const [orderTrackingData, setOrderTrackingData] = useState(null)
  const [isTracking, setIsTracking] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState({})
  const [error, setError] = useState('')
  const requestId = useRef(0)
  const [trackingError, setTrackingError] = useState('')

  const handleTracking = useCallback(
    async (number = trackingNumber) => {
      const lr = String(number || '').trim()

      if (!lr) {
        setError('Please enter your LR number to track your shipment.')
        setTrackingError('')
        setShowResults(false)
        return
      }

      setTrackingNumber(lr)
      setIsTracking(true)
      setError('')
      setTrackingError('')
      setShowResults(false)

      // Clear previous shipment data before starting a new request
      setTrackingSteps([])
      setOrderTrackingData(null)
      setExpandedGroups({})

      try {
        const response = await fetch(
          `${API_URL}trackorder/shipment?lr_no=${encodeURIComponent(lr)}`
        )

        // Parse the body even when the server returns HTTP 404
        const data = await response.json().catch(() => ({}))

        const message = String(data?.message || '').trim()
        const normalizedMessage = message.toLowerCase()

        const isLrNotFound =
          response.status === 404 ||
          data?.success === false ||
          normalizedMessage.includes('lr number not found')

        if (isLrNotFound) {
          setTrackingSteps([])
          setOrderTrackingData(null)
          setTrackingError(message || 'LR number not found')
          setError('')
          setExpandedGroups({})
          setShowResults(true)
          return
        }

        if (!response.ok) {
          throw new Error(
            message || 'Unable to fetch tracking details. Please try again.'
          )
        }

        setTrackingError('')

        const normalized = normalizeTrackingData(data) || []

        setTrackingSteps(normalized)

        setOrderTrackingData({
          lr_no: data?.source_lr_no || data?.data?.[0]?.lr_no || lr,
          order_status:
            data?.database_data?.order_status ||
            data?.data?.[0]?.order_status ||
            '',
          pod:
            data?.tracking?.data?.details?.[0]?.pod ||
            data?.tracking?.orderInformation?.pod_links?.[0] ||
            data?.data?.[0]?.sinfinite_pod ||
            null,
          forward_company: data?.data?.[0]?.forward_company || null,
          forward_lr: data?.data?.[0]?.forward_lr || null
        })

        setExpandedGroups({})
        setShowResults(true)
      } catch (err) {
        console.error('Tracking error:', err)

        setError(err.message || 'Something went wrong. Please try again.')

        setTrackingError('')
        setTrackingSteps([])
        setOrderTrackingData(null)
        setExpandedGroups({})
        setShowResults(false)
      } finally {
        setIsTracking(false)
      }
    },
    [trackingNumber]
  )

  useEffect(() => {
    if (lrno) {
      setTrackingNumber(lrno)
      handleTracking(lrno)
    }
  }, [lrno, handleTracking])

  const currentStep = [...trackingSteps].sort((a, b) => {
    const first = new Date(a.rawDate || a.time || 0).getTime()
    const second = new Date(b.rawDate || b.time || 0).getTime()
    return second - first
  })[0]

  const groupedSteps = trackingSteps.reduce((groups, step) => {
    const key = step.title || 'Tracking Update'

    if (!groups[key]) groups[key] = []

    groups[key].push(step)

    return groups
  }, {})

  const orderedGroups = STATUS_ORDER.filter(
    status => groupedSteps[status]?.length
  ).map(status => ({
    status,
    items: [...groupedSteps[status]].sort((a, b) => {
      const first = new Date(a.rawDate || a.time || 0).getTime()
      const second = new Date(b.rawDate || b.time || 0).getTime()

      return second - first
    })
  }))

  const copyTrackingLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)

      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Unable to copy the link. Please copy it from the address bar.')
    }
  }

  return (
    <div className='min-h-screen bg-[#f5f6fa] text-slate-800'>
      <Helmet>
        {/* Primary SEO */}
        <title>Track Shipment by LR Number | S Infinite Express</title>

        <meta
          name='description'
          content='Track your shipment online with S Infinite Express using your LR number. Get real-time shipment status, delivery updates, consignment tracking and proof of delivery across India.'
        />

        <meta
          name='keywords'
          content='S Infinite Express tracking, shipment tracking, LR number tracking, consignment tracking, track shipment online, logistics tracking India, transport tracking, delivery status'
        />

        <meta name='robots' content='index, follow, max-image-preview:large' />
        <meta name='author' content='S Infinite Express Pvt. Ltd.' />
        <meta name='viewport' content='width=device-width, initial-scale=1.0' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/track-shipment'
        />

        {/* Open Graph — Facebook, LinkedIn and social sharing */}
        <meta property='og:locale' content='en_IN' />
        <meta property='og:type' content='website' />
        <meta property='og:site_name' content='S Infinite Express' />
        <meta
          property='og:title'
          content='Track Your Shipment Online | S Infinite Express'
        />
        <meta
          property='og:description'
          content='Track your consignment by LR number with S Infinite Express. Check shipment progress, tracking updates and delivery status online.'
        />
        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/track-shipment'
        />
        <meta
          property='og:image'
          content='https://sinfiniteexpress.com/assets/logo/logo.png'
        />
        <meta
          property='og:image:alt'
          content='S Infinite Express logistics and shipment tracking'
        />

        {/* Twitter / X */}
        <meta name='twitter:card' content='summary_large_image' />
        <meta
          name='twitter:title'
          content='Track Shipment by LR Number | S Infinite Express'
        />
        <meta
          name='twitter:description'
          content='Track your shipment online using your LR number. Get delivery progress and consignment status with S Infinite Express.'
        />
        <meta
          name='twitter:image'
          content='https://sinfiniteexpress.com/assets/logo/logo.png'
        />
      </Helmet>

      {/* Premium hero and tracking search */}
      <section className='relative isolate overflow-hidden bg-[#211f57] px-4 pb-16 pt-8 text-white sm:pb-10 sm:pt-5'>
        <div className='pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full border border-white/10' />
        <div className='pointer-events-none absolute -right-8 -top-12 h-56 w-56 rounded-full border border-white/10' />
        <div className='pointer-events-none absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-white/[0.04]' />

        <div className='relative mx-auto max-w-5xl text-center'>
          <div className='mt-1 flex flex-wrap justify-center gap-1 text-xs text-indigo-100/90'>
            <span className='flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-3 py-2'>
              <CheckCircle size={14} /> Easy tracking
            </span>
            <span className='flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-3 py-2'>
              <Clock size={14} /> Shipment updates
            </span>
            <span className='flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-3 py-2'>
              <MapPin size={14} /> Location visibility
            </span>
          </div>
        </div>
      </section>

      {/* Tracking results */}
      {showResults && (
        <section className='relative z-10 mx-auto -mt-8 max-w-5xl px-4 pb-12 sm:px-6'>
          {/* Tracking timeline */}
          <div className='overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-900/5'>
            <div className='flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-4 sm:px-6'>
              <div>
                <p className='text-[10px] font-bold uppercase tracking-wider text-slate-400'>
                  Shipment Tracking
                </p>
                <h2 className='mt-1 text-lg font-extrabold text-[#211f57]'>
                  Milestone History :{' '}
                  {orderTrackingData?.lr_no || trackingNumber}
                </h2>
              </div>

              <div className='flex gap-2'>
                {orderTrackingData?.pod ? (
                  <a
                    href={orderTrackingData.pod}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1.5 rounded-lg bg-[#d32c30] px-3 py-2 text-xs font-semibold text-white hover:bg-[#b82629]'
                  >
                    <Download size={14} /> POD
                  </a>
                ) : (
                  <span className='rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-400'>
                    POD unavailable
                  </span>
                )}

                <button
                  onClick={() => setShowShareModal(true)}
                  className='inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-[#211f57] hover:bg-slate-50'
                >
                  <Share2 size={14} /> Share
                </button>
              </div>
            </div>

            <div className='px-3 py-5 sm:px-6'>
              {orderedGroups.length ? (
                <div className='relative space-y-3 before:absolute before:bottom-4 before:left-[13px] before:top-4 before:w-px before:bg-slate-200'>
                  {orderedGroups.map(({ status, items }) => {
                    const step = items[0]
                    const color =
                      STATUS_COLORS[status.toLowerCase()] || '#64748b'
                    const expanded = !!expandedGroups[status]

                    return (
                      <article key={status} className='relative flex gap-3'>
                        <div
                          className='relative z-10 mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-2 border-white text-white shadow-sm'
                          style={{ backgroundColor: color }}
                        >
                          {React.cloneElement(getStatusIcon(status), {
                            size: 13
                          })}
                        </div>

                        <div className='min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 transition hover:border-slate-300 hover:shadow-sm'>
                          <div className='flex flex-wrap items-start justify-between gap-2'>
                            <span
                              className='text-sm font-bold'
                              style={{ color }}
                            >
                              {status}
                            </span>

                            {items.length > 1 && (
                              <button
                                onClick={() =>
                                  setExpandedGroups(previous => ({
                                    ...previous,
                                    [status]: !previous[status]
                                  }))
                                }
                                className='text-[10px] font-bold text-[#211f57] hover:text-[#d32c30]'
                              >
                                {expanded
                                  ? 'Hide previous updates −'
                                  : `View ${items.length - 1} previous update${
                                      items.length > 2 ? 's' : ''
                                    } +`}
                              </button>
                            )}
                          </div>

                          <p className='mt-2 flex items-start gap-2 text-xs font-medium leading-5 text-slate-700'>
                            <MapPin
                              size={14}
                              className='mt-0.5 shrink-0 text-slate-400'
                            />
                            <span className='break-words'>
                              {step.description} —{' '}
                              {step.location || 'Location not available'}
                            </span>
                          </p>

                          <p className='mt-2 text-[11px] font-semibold text-slate-400'>
                            {formatTime(step.time)}
                          </p>

                          {expanded && items.length > 1 && (
                            <div className='mt-3 space-y-3 border-t border-slate-100 pt-3'>
                              {items.slice(1).map((item, index) => (
                                <div
                                  key={item.tracking_id ?? `${status}-${index}`}
                                  className='border-l-2 border-slate-200 pl-3'
                                >
                                  <p className='text-[10px] font-semibold text-slate-400'>
                                    {formatTime(item.time)}
                                  </p>
                                  <p className='mt-1 text-xs text-slate-600'>
                                    {item.description} —{' '}
                                    {item.location || 'Location not available'}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </article>
                    )
                  })}
                </div>
              ) : (
                <div className='px-4 py-12 text-center'>
                  <Package className='mx-auto text-slate-300' size={36} />

                  <h3 className='mt-3 text-base font-bold text-[#211f57]'>
                    {trackingError.toLowerCase().includes('not found')
                      ? 'LR Number Not Found'
                      : 'No Tracking History Found'}
                  </h3>

                  <p className='mt-2 text-sm text-slate-500'>
                    {trackingError.toLowerCase().includes('not found')
                      ? `We couldn't find LR number ${trackingNumber}. Please check the number and try again.`
                      : "We couldn't find tracking updates for this LR number."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Share modal */}
      {showShareModal && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-[#171638]/60 p-4 backdrop-blur-sm'
          onClick={event => {
            if (event.target === event.currentTarget) setShowShareModal(false)
          }}
        >
          <div className='relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-7'>
            <button
              onClick={() => setShowShareModal(false)}
              aria-label='Close share dialog'
              className='absolute right-4 top-4 rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700'
            >
              <X size={20} />
            </button>

            {/* <h2 className="mt-4 text-xl font-extrabold text-[#211f57]">
              Share tracking details
            </h2> */}
            <p className='mt-1 text-sm text-slate-500'>
              Share the shipment tracking link with anyone.
            </p>

            <div className='mt-5 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3'>
              <span className='min-w-0 flex-1 break-all text-xs text-slate-600'>
                {window.location.href}
              </span>
              <button
                onClick={copyTrackingLink}
                className='shrink-0 rounded-lg bg-[#211f57] px-3 py-2 text-xs font-bold text-white hover:bg-[#302c76]'
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            <p className='mt-5 text-xs font-bold uppercase tracking-wider text-slate-400'>
              Share using
            </p>
            <div className='mt-3 grid grid-cols-3 gap-3'>
              {[
                {
                  name: 'WhatsApp',
                  image: whatsApp,
                  href: `https://wa.me/?text=${encodeURIComponent(
                    `Track your shipment: ${window.location.href}`
                  )}`
                },
                {
                  name: 'Email',
                  image: Gmail,
                  href: `mailto:?subject=${encodeURIComponent(
                    'Track Shipment'
                  )}&body=${encodeURIComponent(
                    `Track your shipment here: ${window.location.href}`
                  )}`
                },
                {
                  name: 'Telegram',
                  image: Telegram,
                  href: `https://t.me/share/url?url=${encodeURIComponent(
                    window.location.href
                  )}&text=${encodeURIComponent('Track your shipment')}`
                }
              ].map(item => (
                <a
                  key={item.name}
                  href={item.href}
                  target={item.name === 'Email' ? undefined : '_blank'}
                  rel={
                    item.name === 'Email' ? undefined : 'noopener noreferrer'
                  }
                  className='flex flex-col items-center gap-2 rounded-xl border border-slate-100 p-3 transition hover:border-slate-200 hover:bg-slate-50'
                >
                  <img
                    src={item.image}
                    alt=''
                    className='h-9 w-9 object-contain'
                  />
                  <span className='text-xs font-semibold text-slate-700'>
                    {item.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* <Faq /> */}
      <FooterPage />
    </div>
  )
}
